import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAddressBook, type SavedAddress } from "@/lib/account/addresses";
import { AddressEditor, EMPTY_DRAFT } from "@/components/account/AddressEditor";
import { DeviceAddressesNotice } from "@/components/account/DeviceAddressesNotice";
import { AccountShell, SectionCard, EmptyState, GoldButton } from "@/components/account/AccountShell";

export const Route = createFileRoute("/account/addresses")({
  head: () => ({
    meta: [
      { title: "Address Book — CB16 Maison" },
      { name: "description", content: "Manage your saved CB16 Maison delivery addresses." },
      { property: "og:title", content: "Address Book — CB16 Maison" },
      { property: "og:description", content: "Manage your saved CB16 Maison delivery addresses." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AddressesPage,
});

function AddressesPage() {
  const book = useAddressBook();
  const [editing, setEditing] = useState<null | { mode: "new" } | { mode: "edit"; address: SavedAddress }>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(fn: () => Promise<void>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <AccountShell>
      <SectionCard
        title="Address Book"
        action={
          editing === null && book.signedIn ? (
            <button
              onClick={() => setEditing({ mode: "new" })}
              className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#C6A15B] hover:underline"
            >
              + Add address
            </button>
          ) : null
        }
      >
        <DeviceAddressesNotice book={book} />
        {(book.error || actionError) && (
          <p role="alert" className="mb-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {actionError ?? book.error}
          </p>
        )}
        {editing && (
          <div className="mb-8">
            <AddressEditor
              key={editing.mode === "edit" ? editing.address.id : "new"}
              initial={editing.mode === "edit" ? { ...editing.address } : EMPTY_DRAFT}
              mode={editing.mode}
              onCancel={() => setEditing(null)}
              onSave={async (values) => {
                await book.save({ ...values, id: editing.mode === "edit" ? editing.address.id : null });
                setEditing(null);
              }}
            />
          </div>
        )}

        {book.loading ? (
          <p className="text-sm text-black/50">Loading your addresses…</p>
        ) : book.addresses.length === 0 && !editing ? (
          <EmptyState
            title="No saved addresses yet"
            description="Add a delivery address to speed up future checkouts."
            action={<GoldButton onClick={() => setEditing({ mode: "new" })}>Add address</GoldButton>}
          />
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {book.addresses.map((a) => (
              <li key={a.id} className="border border-black/10 p-5">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/70">{a.label}</p>
                  {a.isDefault && (
                    <span className="border border-[#C6A15B] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.2em] text-[#C6A15B]">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-sm text-black">{a.fullName}</p>
                <p className="text-sm text-black/70">{a.line1}</p>
                {a.line2 && <p className="text-sm text-black/70">{a.line2}</p>}
                <p className="text-sm text-black/70">
                  {a.city}{a.state ? `, ${a.state}` : ""} {a.postalCode}
                </p>
                <p className="text-sm text-black/70">{a.country}</p>
                <p className="mt-1 text-xs text-black/50">{a.phone}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-[11px] font-medium uppercase tracking-[0.22em]">
                  <button className="text-black/70 hover:text-[#C6A15B]" onClick={() => setEditing({ mode: "edit", address: a })}>
                    Edit
                  </button>
                  {!a.isDefault && (
                    <button className="text-black/70 hover:text-[#C6A15B]" onClick={() => void act(() => book.setDefault(a.id))}>
                      Set default
                    </button>
                  )}
                  <button
                    className="text-red-600/80 hover:text-red-600"
                    onClick={() => {
                      if (window.confirm(`Remove the ${a.label} address?`)) void act(() => book.remove(a.id));
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </AccountShell>
  );
}
